import React, { useEffect, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import {
  Box,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { ChartColumn } from 'lucide-react';
import { dashboardAPI } from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const CHART_HEIGHT = 400;

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'threeMonths', label: 'Last 3 Months' },
  { value: 'sixMonths', label: 'Last 6 Months' },
  { value: 'year', label: 'This Year' },
];

const SalesGraph = () => {
  const { theme } = useTheme();
  const mantine = useMantineTheme();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState<
    'today' | 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year'
  >('sixMonths');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getSalesGraph(currentTime);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentTime]);

  const isDark = theme === 'dark';

  // The chart is drawn on a canvas, so it cannot read CSS variables: its
  // colours are picked from the Mantine palette for the scheme on screen.
  const { colors, white, black, primaryColor, fontFamily } = mantine;
  const textColor = isDark ? colors.dark[1] : colors.gray[7];
  const gridColor = isDark ? colors.dark[5] : colors.gray[2];
  const axisColor = isDark ? colors.dark[4] : colors.gray[4];
  const surfaceColor = isDark ? colors.dark[6] : white;
  const series = colors[primaryColor];

  const options = {
    animationEnabled: true,
    height: CHART_HEIGHT,
    theme: isDark ? 'dark2' : 'light2',
    backgroundColor: 'transparent',
    title: {
      fontSize: 0,
    },
    axisX: {
      valueFormatString: 'DD MMM',
      labelAngle: -45,
      intervalType: 'minute',
      gridColor,
      lineColor: axisColor,
      tickColor: axisColor,
      labelFontFamily: fontFamily,
      labelFontSize: 12,
      labelFontColor: textColor,
    },
    axisY: {
      prefix: 'PKR ',
      gridColor,
      lineColor: axisColor,
      tickColor: axisColor,
      labelFontFamily: fontFamily,
      labelFontSize: 12,
      labelFontColor: textColor,
    },
    toolTip: {
      shared: true,
      backgroundColor: surfaceColor,
      fontColor: isDark ? white : black,
      borderThickness: 0,
      cornerRadius: 8,
      fontFamily,
      contentFormatter: function (e: any) {
        return `📅 ${e.entries[0].dataPoint.x.toLocaleString()} <br/><strong>PKR ${e.entries[0].dataPoint.y.toFixed(
          2
        )}</strong>`;
      },
    },
    data: [
      {
        yValueFormatString: 'PKR #,###',
        xValueFormatString: 'DD MMM HH:mm',
        type: 'area',
        fillOpacity: 0.3,
        markerSize: 6,
        markerType: 'circle',
        lineColor: series[isDark ? 5 : 7],
        color: series[isDark ? 4 : 5],
        lineThickness: 3,
        dataPoints: stats
          ?.map((stat: any) => ({
            x: new Date(stat.date),
            y: stat.total,
          }))
          .sort((a: any, b: any) => a.x - b.x),
      },
    ],
  };

  return (
    <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
      <Group justify="space-between" mb="lg" gap="sm">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="lg" variant="light">
            <ChartColumn size={18} />
          </ThemeIcon>
          <Title order={4}>Sales Overview</Title>
        </Group>

        <Select
          aria-label="Period"
          data={RANGES}
          value={currentTime}
          onChange={(value) => value && setCurrentTime(value as any)}
          allowDeselect={false}
          w={170}
        />
      </Group>

      <Box mih={CHART_HEIGHT}>
        {!loading ? (
          <CanvasJSChart options={options} />
        ) : (
          <Stack align="center" justify="center" gap="xs" h={CHART_HEIGHT}>
            <Loader />
            <Text size="sm" c="dimmed">
              Loading sales data...
            </Text>
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default SalesGraph;
